import type { FC, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'

import { forwardRef } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

const control = 'w-full rounded-xl border border-input bg-white px-3.5 text-[15px] text-navy-950 shadow-[0_1px_0_rgb(8_18_41/0.02)] transition-colors placeholder:text-navy-400 hover:border-navy-300 focus:border-cyan-500 focus:outline-none focus:ring-4 focus:ring-cyan-400/20 disabled:cursor-not-allowed disabled:bg-surface disabled:text-navy-400 aria-[invalid=true]:border-red-400 aria-[invalid=true]:focus:ring-red-400/20'

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
    <input ref={ref} className={cn(control, 'h-11', className)} {...props} />
))

Input.displayName = 'Input'

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, rows = 3, ...props }, ref) => (
    <textarea ref={ref} rows={rows} className={cn(control, 'min-h-24 py-2.5 leading-relaxed', className)} {...props} />
))

Textarea.displayName = 'Textarea'

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, children, ...props }, ref) => (
    <div className='relative'>
        <select ref={ref} className={cn(control, 'h-11 appearance-none pe-10', className)} {...props}>
            {children}
        </select>
        <ChevronDown className='pointer-events-none absolute end-3.5 top-1/2 size-4 -translate-y-1/2 text-navy-400' aria-hidden='true' />
    </div>
))

Select.displayName = 'Select'

interface FieldProps {
    label: ReactNode
    htmlFor: string
    hint?: ReactNode
    error?: string | null
    required?: boolean
    className?: string
    children: ReactNode
}

export const Field: FC<FieldProps> = ({ label, htmlFor, hint, error, required, className, children }) => {
    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <label htmlFor={htmlFor} className='text-sm font-medium text-navy-900'>
                {label}
                {required && <span className='ms-0.5 text-red-500' aria-hidden='true'>*</span>}
            </label>

            {children}

            {error
                ? <p id={`${htmlFor}-error`} className='text-xs font-medium text-red-600' role='alert'>{error}</p>
                : hint
                    ? <p id={`${htmlFor}-hint`} className='text-xs leading-relaxed text-muted-foreground'>{hint}</p>
                    : null}
        </div>
    )
}

interface SwitchProps {
    checked: boolean
    onChange: (checked: boolean) => void
    label: ReactNode
    description?: ReactNode
    id: string
    disabled?: boolean
    className?: string
}

export const Switch: FC<SwitchProps> = ({ checked, onChange, label, description, id, disabled, className }) => {
    return (
        <div className={cn('flex items-start justify-between gap-4', className)}>
            <div className='flex flex-col gap-0.5'>
                <label htmlFor={id} className='cursor-pointer text-sm font-medium text-navy-900'>{label}</label>
                {description && <p className='text-xs leading-relaxed text-muted-foreground'>{description}</p>}
            </div>

            <button
                id={id}
                type='button'
                role='switch'
                aria-checked={checked}
                disabled={disabled}
                onClick={() => onChange(!checked)}
                className={cn(
                    'relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-400/30 disabled:cursor-not-allowed disabled:opacity-50',
                    checked ? 'bg-cyan-500' : 'bg-navy-200'
                )}
            >
                <span
                    className={cn(
                        'inline-block size-5 rounded-full bg-white shadow-sm transition-transform duration-200',
                        checked ? 'translate-x-6 rtl:-translate-x-6' : 'translate-x-1 rtl:-translate-x-1'
                    )}
                />
            </button>
        </div>
    )
}

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label: ReactNode
}

export const Checkbox: FC<CheckboxProps> = ({ label, className, id, ...props }) => {
    return (
        <label htmlFor={id} className={cn('flex cursor-pointer items-start gap-3 text-sm text-navy-800', className)}>
            <input id={id} type='checkbox' className='mt-0.5 size-4.5 shrink-0 cursor-pointer rounded border-navy-300 accent-cyan-600' {...props} />
            <span className='leading-relaxed'>{label}</span>
        </label>
    )
}
